export type MeterTone = "positive" | "info" | "neutral" | "caution";

export type ShareBucket = "exact" | "under_one" | "over_ninety_nine";

export interface MeterSegment {
  /** Stable identity for the segment; used as the React key. */
  key: string;
  /** Translated name shown in the legend beside the segment's swatch. */
  label: string;
  /** Raw magnitude in the same unit as `total`. Zero and negative values are dropped. */
  value: number;
  /** Pre-formatted, locale-aware amount rendered in the legend. */
  formattedValue: string;
  /** Semantic tone; the component maps it to a library token. Callers never pass colours. */
  tone: MeterTone;
}

export interface LaidOutSegment extends MeterSegment {
  /** Unrounded fraction of the total, used for the track's flex ratios. */
  share: number;
  /** Rounded percentage for display, except in the two clamped buckets. */
  percent: number;
  bucket: ShareBucket;
}

export interface SegmentStyle {
  flexGrow: number;
  flexBasis: number;
  minWidth: number;
}

/**
 * The narrowest a non-zero segment may render. Applied as a CSS `min-width`, so
 * the flex siblings give up the difference rather than the arithmetic having to
 * redistribute it.
 */
export const MIN_SEGMENT_PX = 3;

/**
 * A segment's share of `total`, bucketed so a share can never round into a lie:
 * a non-zero amount never reads 0%, and an amount short of the total never
 * reads 100%.
 */
export function shareOf(
  value: number,
  total: number,
): { percent: number; bucket: ShareBucket } {
  if (total <= 0) return { percent: 0, bucket: "exact" };

  const raw = (value / total) * 100;
  const rounded = Math.round(raw);

  if (raw > 0 && rounded === 0) return { percent: raw, bucket: "under_one" };
  if (raw < 100 && rounded === 100) {
    return { percent: raw, bucket: "over_ninety_nine" };
  }

  return { percent: rounded, bucket: "exact" };
}

/**
 * Drops empty segments and resolves each survivor's share of `total`.
 *
 * Two of the meter's honesty rules live here so no call site can re-implement
 * them: a segment worth nothing is absent entirely rather than drawn as a
 * sliver, and every share is bucketed by {@link shareOf}.
 */
export function layoutSegments(
  segments: MeterSegment[],
  total: number,
): LaidOutSegment[] {
  if (total <= 0) return [];

  return segments
    .filter((segment) => segment.value > 0)
    .map((segment) => ({
      ...segment,
      share: segment.value / total,
      ...shareOf(segment.value, total),
    }));
}

/**
 * The track geometry for one segment. Width is expressed as a flex ratio rather
 * than a percentage so that {@link MIN_SEGMENT_PX} can hold a tiny segment
 * visible while its siblings absorb the difference — which a percentage width
 * cannot do without overflowing the track.
 */
export function segmentStyle(segment: LaidOutSegment): SegmentStyle {
  return {
    flexGrow: segment.share,
    flexBasis: 0,
    minWidth: MIN_SEGMENT_PX,
  };
}
